// src/components/BodyFatProjection.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useApp } from "../App";
import { getWeightEntries, updateUserProfile } from "../services/firebase";
import { estimateBodyFat, projectBodyComposition } from "../utils/bodyFatEstimator";

export default function BodyFatProjection() {
  const { user, profile, refreshProfile } = useApp();

  const [weights,    setWeights]    = useState([]);
  const [,           setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState("");
  const [success,    setSuccess]    = useState("");
  const [showForm,   setShowForm]   = useState(false);

  // Edit form state
  const [form, setForm] = useState({
    method:       "navy",   // "navy" | "manual" | "none"
    neck_inches:  profile?.neck_inches  || "",
    waist_inches: profile?.waist_inches || "",
    hip_inches:   profile?.hip_inches   || "",
    body_fat_manual: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (profile) {
      setForm((f) => ({
        ...f,
        neck_inches:  profile.neck_inches  || "",
        waist_inches: profile.waist_inches || "",
        hip_inches:   profile.hip_inches   || "",
      }));
    }
  }, [profile]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await getWeightEntries(user.uid, 90);
    setWeights(data || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Derived: current weight
  const currentWeight = weights.length > 0
    ? weights[weights.length - 1].weight
    : profile?.weight_start;

  // Body fat estimate from Navy method
  const navyEstimate = profile?.neck_inches && profile?.waist_inches
    ? estimateBodyFat({
        sex:          profile.sex,
        neck_inches:  profile.neck_inches,
        waist_inches: profile.waist_inches,
        hip_inches:   profile.hip_inches,
        height_inches: profile.height_inches,
      })
    : null;

  const bodyFatPct = profile?.body_fat_pct ?? navyEstimate;

  // Projection data
  const lossPerWeek = 1.0; // default; could derive from actual 14-day trend
  const projectionData = bodyFatPct && currentWeight && profile?.weight_goal
    ? projectBodyComposition({
        current_weight_lbs: currentWeight,
        body_fat_pct:       bodyFatPct,
        goal_weight_lbs:    profile.weight_goal,
        weekly_loss_lbs:    lossPerWeek,
        protein_per_lb:     profile.protein_per_lb || 0.8,
      })
    : [];

  // Fat mass and lean mass now
  const fatMassNow  = bodyFatPct ? Math.round((bodyFatPct / 100) * currentWeight * 10) / 10 : null;
  const leanMassNow = fatMassNow !== null ? Math.round((currentWeight - fatMassNow) * 10) / 10 : null;

  // Projected at goal
  const goalPoint = projectionData[projectionData.length - 1];

  // ── Save measurements ────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(""); setSuccess("");
    let bfPct = null;

    if (form.method === "navy") {
      if (!form.neck_inches || !form.waist_inches) {
        setError("Enter neck and waist measurements."); return;
      }
      if (profile?.sex === "female" && !form.hip_inches) {
        setError("Enter hip measurement."); return;
      }
      bfPct = estimateBodyFat({
        sex:           profile.sex,
        neck_inches:   parseFloat(form.neck_inches),
        waist_inches:  parseFloat(form.waist_inches),
        hip_inches:    form.hip_inches ? parseFloat(form.hip_inches) : null,
        height_inches: profile.height_inches,
      });
      if (!bfPct) { setError("Could not calculate body fat. Check your measurements."); return; }
    } else if (form.method === "manual") {
      const v = parseFloat(form.body_fat_manual);
      if (!v || v < 3 || v > 60) { setError("Enter a valid body fat % (3–60)."); return; }
      bfPct = v;
    }

    setSaving(true);
    const updates = {
      body_fat_pct:  bfPct,
      neck_inches:   form.method === "navy" ? parseFloat(form.neck_inches)  : profile.neck_inches,
      waist_inches:  form.method === "navy" ? parseFloat(form.waist_inches) : profile.waist_inches,
      hip_inches:    form.method === "navy" && form.hip_inches
                       ? parseFloat(form.hip_inches) : profile.hip_inches,
    };
    const { error: err } = await updateUserProfile(user.uid, updates);
    if (err) { setError(err); setSaving(false); return; }

    await refreshProfile();
    setSuccess("Measurements saved!");
    setShowForm(false);
    setSaving(false);
  };

  // ── Chart formatting ─────────────────────────────────────────────────────
  const chartData = projectionData.map((pt) => ({
    week:     `W${pt.week}`,
    "Fat Mass (lbs)":  pt.fat_mass,
    "Lean Mass (lbs)": pt.lean_mass,
    "BF%":             pt.body_fat_pct,
  }));

  return (
    <div className="space-y-5 animate-slide-up">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="section-title">Body Fat Projection</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Estimated body composition over your cut
          </p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="btn-ghost text-sm">
          {showForm ? "Cancel" : bodyFatPct ? "Update" : "Add Measurements"}
        </button>
      </div>

      {/* ── Disclaimer ───────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 bg-[#111820] border border-[#263347] rounded-xl px-4 py-3">
        <span className="text-lg flex-shrink-0">📊</span>
        <p className="text-xs text-slate-500">
          All body composition figures are <strong className="text-slate-400">estimates only</strong>,
          not medical data. Use for general trend awareness. Consult a healthcare professional
          for clinical measurements.
        </p>
      </div>

      {/* ── Measurement form ─────────────────────────────────────────── */}
      {showForm && (
        <div className="card animate-slide-up">
          <div className="text-sm font-semibold text-slate-200 mb-4">Update Body Measurements</div>

          {/* Method selector */}
          <div className="space-y-2 mb-4">
            {[
              { v: "navy",   l: "Navy Method",     d: "Calculate from neck / waist / hip" },
              { v: "manual", l: "Enter manually",  d: "If you already know your BF%" },
            ].map(({ v, l, d }) => (
              <button key={v} type="button" onClick={() => set("method", v)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                  form.method === v
                    ? "bg-[#ff7c0a]/10 border-[#ff7c0a]/50"
                    : "bg-[#111820] border-[#263347] hover:border-slate-500"
                }`}>
                <div className="text-sm font-semibold text-slate-200">{l}</div>
                <div className="text-xs text-slate-500 mt-0.5">{d}</div>
              </button>
            ))}
          </div>

          {form.method === "navy" && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Measure at the widest point. Use a soft tape measure pulled snug but not compressing skin.
              </p>
              <div>
                <label className="label">Neck (inches)</label>
                <input className="input" type="number" placeholder="15.0" step="0.25"
                  value={form.neck_inches} onChange={(e) => set("neck_inches", e.target.value)} />
              </div>
              <div>
                <label className="label">Waist (inches) — at navel</label>
                <input className="input" type="number" placeholder="34.0" step="0.25"
                  value={form.waist_inches} onChange={(e) => set("waist_inches", e.target.value)} />
              </div>
              {profile?.sex === "female" && (
                <div>
                  <label className="label">Hips (inches) — widest point</label>
                  <input className="input" type="number" placeholder="40.0" step="0.25"
                    value={form.hip_inches} onChange={(e) => set("hip_inches", e.target.value)} />
                </div>
              )}
              {/* Live preview */}
              {form.neck_inches && form.waist_inches && (
                <div className="bg-[#0d1117] border border-[#1f2a3c] rounded-lg px-4 py-3">
                  <div className="text-xs text-slate-500 mb-1">Estimated Body Fat %</div>
                  <div className="text-2xl font-bold text-[#ff7c0a]"
                       style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {estimateBodyFat({
                      sex:           profile?.sex,
                      neck_inches:   parseFloat(form.neck_inches),
                      waist_inches:  parseFloat(form.waist_inches),
                      hip_inches:    form.hip_inches ? parseFloat(form.hip_inches) : null,
                      height_inches: profile?.height_inches,
                    }) ?? "—"}%
                  </div>
                </div>
              )}
            </div>
          )}

          {form.method === "manual" && (
            <div>
              <label className="label">Body Fat % (e.g. 22)</label>
              <input className="input" type="number" placeholder="22" min="3" max="60"
                value={form.body_fat_manual} onChange={(e) => set("body_fat_manual", e.target.value)} />
            </div>
          )}

          {error   && <p className="text-red-400 text-xs mt-3">{error}</p>}
          {success && <p className="text-emerald-400 text-xs mt-3">{success}</p>}

          <button onClick={handleSave} disabled={saving} className="btn-primary w-full mt-4">
            {saving ? "Saving…" : "Save Measurements"}
          </button>
        </div>
      )}

      {/* ── No data state ─────────────────────────────────────────────── */}
      {!bodyFatPct && !showForm && (
        <div className="card text-center py-12 text-slate-500">
          <span className="text-4xl block mb-3">📐</span>
          <p className="text-sm font-medium text-slate-300">No body fat data yet</p>
          <p className="text-xs mt-1 mb-4">
            Add measurements using the Navy Method or enter your body fat % manually.
          </p>
          <button onClick={() => setShowForm(true)} className="btn-primary mx-auto">
            Add Measurements
          </button>
        </div>
      )}

      {/* ── Current stats ─────────────────────────────────────────────── */}
      {bodyFatPct && (
        <div className="card">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-4">Current Estimates</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatTile
              label="Body Fat"
              value={`${bodyFatPct}%`}
              color="text-[#ff7c0a]"
              sub={bfCategory(bodyFatPct, profile?.sex)}
            />
            <StatTile
              label="Fat Mass"
              value={fatMassNow !== null ? `${fatMassNow} lbs` : "—"}
              color="text-red-400"
            />
            <StatTile
              label="Lean Mass"
              value={leanMassNow !== null ? `${leanMassNow} lbs` : "—"}
              color="text-emerald-400"
              sub="muscle + bone + water"
            />
            <StatTile
              label="Body Weight"
              value={currentWeight ? `${currentWeight} lbs` : "—"}
              color="text-slate-100"
            />
          </div>

          {/* Method badge */}
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
            <span>Source:</span>
            <span className="badge-blue">
              {profile?.neck_inches ? "Navy Method" : "Manual Entry"}
            </span>
          </div>
        </div>
      )}

      {/* ── Projection chart ──────────────────────────────────────────── */}
      {projectionData.length > 1 && (
        <div className="card">
          <div className="flex items-start justify-between mb-1">
            <div className="text-sm font-semibold text-slate-200">Body Composition Projection</div>
            <span className="badge-blue text-[10px]">estimate</span>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Projected fat vs lean mass trajectory at ~{lossPerWeek} lb/week loss.
            High protein intake (≥0.8g/lb) is modeled to preserve more lean mass.
          </p>

          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="gradFat"  x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0.0} />
                </linearGradient>
                <linearGradient id="gradLean" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2a3c" />
              <XAxis dataKey="week" tick={{ fill: "#64748b", fontSize: 10 }}
                tickLine={false} axisLine={false} interval={Math.floor(chartData.length / 6)} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }}
                tickLine={false} axisLine={false} />
              <Tooltip content={<ProjectionTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: "11px", color: "#94a3b8" }}
                iconType="circle" iconSize={8}
              />
              <Area
                type="monotone" dataKey="Lean Mass (lbs)"
                stroke="#10b981" strokeWidth={2}
                fill="url(#gradLean)" dot={false}
              />
              <Area
                type="monotone" dataKey="Fat Mass (lbs)"
                stroke="#ef4444" strokeWidth={2}
                fill="url(#gradFat)" dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Goal state comparison ─────────────────────────────────────── */}
      {goalPoint && bodyFatPct && (
        <div className="card">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-4">
            Projected at Goal Weight ({profile?.weight_goal} lbs)
          </div>
          <div className="grid grid-cols-2 gap-4">
            <ComparisonColumn
              label="Now"
              weight={currentWeight}
              fatMass={fatMassNow}
              leanMass={leanMassNow}
              bfPct={bodyFatPct}
            />
            <ComparisonColumn
              label="At Goal"
              weight={goalPoint.weight}
              fatMass={goalPoint.fat_mass}
              leanMass={goalPoint.lean_mass}
              bfPct={goalPoint.body_fat_pct}
              highlight
            />
          </div>

          <div className="mt-4 pt-4 border-t border-[#1f2a3c]">
            <div className="text-xs text-slate-500 mb-2">Projected changes</div>
            <div className="grid grid-cols-3 gap-3">
              <DeltaTile
                label="Fat Lost"
                value={`−${(fatMassNow - goalPoint.fat_mass).toFixed(1)} lbs`}
                color="text-emerald-400"
              />
              <DeltaTile
                label="Lean Preserved"
                value={`${((goalPoint.lean_mass / leanMassNow) * 100).toFixed(0)}%`}
                color="text-blue-400"
              />
              <DeltaTile
                label="BF% Change"
                value={`−${(bodyFatPct - goalPoint.body_fat_pct).toFixed(1)}%`}
                color="text-[#ff7c0a]"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Protein note ──────────────────────────────────────────────── */}
      {bodyFatPct && (
        <div className="card border-[#ff7c0a]/20 bg-[#ff7c0a]/5">
          <div className="flex items-start gap-3">
            <span className="text-xl flex-shrink-0">🥩</span>
            <div>
              <div className="text-sm font-semibold text-[#ff7c0a] mb-1">
                Protein protects your lean mass
              </div>
              <div className="text-xs text-slate-400">
                This projection assumes{" "}
                <strong className="text-slate-300">{profile?.protein_per_lb || 0.8}g protein/lb</strong>.
                At this intake, approximately{" "}
                <strong className="text-slate-300">80%</strong> of weight lost
                comes from fat rather than muscle.
                Hit your daily protein target to keep this ratio as high as possible.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatTile({ label, value, color, sub }) {
  return (
    <div className="bg-[#111820] rounded-lg p-3 text-center">
      <div className={`text-xl font-bold ${color}`}
           style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        {value}
      </div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-slate-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function ComparisonColumn({ label, weight, fatMass, leanMass, bfPct, highlight }) {
  return (
    <div className={`rounded-lg p-4 ${highlight ? "bg-emerald-950/30 border border-emerald-500/20" : "bg-[#111820]"}`}>
      <div className={`text-xs font-semibold uppercase tracking-wider mb-3 ${highlight ? "text-emerald-400" : "text-slate-500"}`}>
        {label}
      </div>
      {[
        { l: "Weight",    v: `${weight} lbs`       },
        { l: "Fat Mass",  v: `${fatMass} lbs`      },
        { l: "Lean Mass", v: `${leanMass} lbs`     },
        { l: "BF%",       v: `${bfPct}%`           },
      ].map(({ l, v }) => (
        <div key={l} className="flex justify-between text-xs py-1 border-b border-[#1f2a3c] last:border-0">
          <span className="text-slate-500">{l}</span>
          <span className="text-slate-200 font-medium">{v}</span>
        </div>
      ))}
    </div>
  );
}

function DeltaTile({ label, value, color }) {
  return (
    <div className="bg-[#111820] rounded-lg p-3 text-center">
      <div className={`text-lg font-bold ${color}`}
           style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        {value}
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

const ProjectionTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a2332] border border-[#263347] rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-slate-400 mb-1.5">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }} className="flex justify-between gap-4">
          <span>{p.dataKey}</span>
          <span className="font-semibold">{p.value} lbs</span>
        </div>
      ))}
    </div>
  );
};

// ─── Body fat category helper ─────────────────────────────────────────────────
function bfCategory(pct, sex) {
  if (!pct) return "";
  if (sex === "male") {
    if (pct < 6)  return "Essential fat";
    if (pct < 14) return "Athletic";
    if (pct < 18) return "Fitness";
    if (pct < 25) return "Average";
    return "Above average";
  } else {
    if (pct < 14) return "Essential fat";
    if (pct < 21) return "Athletic";
    if (pct < 25) return "Fitness";
    if (pct < 32) return "Average";
    return "Above average";
  }
}
